import { Controller } from "@hotwired/stimulus"

/**
 * Handles AI topic generation for series production queue.
 *
 * Targets:
 * - textarea: the bulk-add textarea to populate
 * - button: the generate button (for loading state)
 * - label: the button label text
 * - spinner: the loading spinner element
 * - icon: the button icon element
 *
 * Values:
 * - url: the generate_topics endpoint URL
 * - count: number of topics to generate (default 10)
 */
export default class TopicGeneratorController extends Controller {
  static targets = ["textarea", "button", "label", "spinner", "icon"]
  static values = { url: String, count: { type: Number, default: 10 } }

  async generate(event) {
    event.preventDefault()

    this.setLoading(true)

    try {
      const url = `${this.urlValue}?count=${this.countValue}`
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        }
      })

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`)
      }

      const data = await response.json()

      if (data.topics && data.topics.length > 0) {
        const lines = data.topics.map(t => t.topic)
        const existing = this.textareaTarget.value.trim()
        this.textareaTarget.value = existing
          ? `${existing}\n${lines.join("\n")}`
          : lines.join("\n")
        this.textareaTarget.rows = Math.min(Math.max(lines.length + 1, 3), 15)
      } else {
        alert("No topics generated. Try again.")
      }
    } catch (error) {
      console.error("Topic generation failed:", error)
      alert("Failed to generate topics. Please try again.")
    } finally {
      this.setLoading(false)
    }
  }

  setLoading(loading) {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = loading
    }
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = loading ? "Generating..." : "Generate Topics"
    }
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("hidden", !loading)
    }
    if (this.hasIconTarget) {
      this.iconTarget.classList.toggle("hidden", loading)
    }
  }
}
